/**
 * My profile model types.
 *
 * Elm-ish architecture: Model is pure data, no logic.
 * State machine for loading and displaying the current user's profile.
 *
 * S018 authority: Figma node 39:4695
 *
 * Privacy doctrine:
 * - No raw accountId in rendered output
 * - No session tokens or identifiers exposed
 * - Avatar is identity-minimized (initial + generic asset)
 */
import type { MyProfileSummary } from './port';

/**
 * Logout request state.
 * Note: 'failed' state has no requestId - it was already consumed.
 */
export type LogoutState =
  | { readonly status: 'idle' }
  | { readonly status: 'submitting'; readonly requestId: number }
  | { readonly status: 'failed' };

/**
 * My profile program state.
 */
export type MyProfileModel =
  | {
      readonly status: 'loading';
      readonly activeLoadId: number;
    }
  | {
      readonly status: 'ready';
      readonly activeLoadId: number;
      readonly profile: MyProfileSummary;
      readonly logout: LogoutState;
    }
  | {
      readonly status: 'load_failed';
      readonly activeLoadId: number;
    };

/**
 * Initial model state for useReducer initializer.
 * Production path: init() -> loading generation 1
 *
 * Note: This is used by useReducer as the initializer function.
 * The initial loadId is 1, not 0.
 */
export function createInitialModel(): MyProfileModel {
  return {
    status: 'loading',
    activeLoadId: 1,
  };
}

/**
 * @deprecated Use createInitialModel() for useReducer initializer.
 * Or pass initializer function directly to useReducer.
 */
export function initialModel(): MyProfileModel {
  return {
    status: 'loading',
    activeLoadId: 1,
  };
}

/**
 * Validate a raw profile input.
 * Returns a validated profile or null if invalid.
 * Fails closed without preserving raw adapter errors.
 */
export function createValidatedProfileSummary(
  input: unknown
): MyProfileSummary | null {
  // Type guard
  if (!input || typeof input !== 'object') {
    return null;
  }

  const obj = input as Record<string, unknown>;

  // Validate accountId
  if (typeof obj.accountId !== 'string' || obj.accountId.trim().length === 0) {
    return null;
  }

  // Validate displayName
  if (typeof obj.displayName !== 'string' || obj.displayName.trim().length === 0) {
    return null;
  }

  // Validate age
  // NOTE: age can be 0 per backend contract (0 = not set)
  // Backend: internal/profiles/sponsor_profile_handlers.go:22 "Age int `json:"age,omitempty"` // 0 means not set"
  // OpenAPI: api/openapi/schemas/profiles.yaml:77-82 "description: User's age (0 means not set)"
  if (
    typeof obj.age !== 'number' ||
    !Number.isFinite(obj.age) ||
    obj.age < 0 ||
    !Number.isInteger(obj.age)
  ) {
    return null;
  }

  // Validate city
  if (typeof obj.city !== 'string' || obj.city.trim().length === 0) {
    return null;
  }

  // Validate avatarUrl (must be string, even if empty)
  if (typeof obj.avatarUrl !== 'string') {
    return null;
  }

  // Validate completionPercent
  //
  // D1 / completion_percent optionality contract drift
  //
  // ACT-INDEEP-PRODUCTION-MY-PROFILE-COMPLETION-PERCENT-OPTIONAL01
  //
  // The backend Friend response does not currently include
  // `completion_percent` (explicitly NOT in the backend contract per
  // adapters/apiMyProfilePort.ts ProfileResponseDTO and per
  // api/openapi/schemas/profiles.yaml). The adapter therefore
  // legitimately emits `completionPercent === undefined` for live
  // responses.
  //
  // The deprecated validateProfile() already treats completionPercent
  // as OPTIONAL (see below); the port type also declares
  // `readonly completionPercent: number | undefined;` (port.ts:44).
  // The second-stage validator must therefore mirror that contract:
  // undefined is legal and must pass through; only when a value is
  // actually present do we enforce the integer-in-[0,100] rule.
  //
  // Pre-fix: `typeof undefined !== 'number'` returned null,
  //          collapsing the producer->consumer seam and forcing
  //          useMyProfileProgram into LoadFailed (UI showed
  //          "Не удалось загрузить профиль").
  if (
    obj.completionPercent !== undefined &&
    (
      typeof obj.completionPercent !== 'number' ||
      !Number.isFinite(obj.completionPercent) ||
      obj.completionPercent < 0 ||
      obj.completionPercent > 100 ||
      !Number.isInteger(obj.completionPercent)
    )
  ) {
    return null;
  }

  // Return frozen validated snapshot
  return Object.freeze({
    accountId: obj.accountId.trim(),
    displayName: obj.displayName.trim(),
    age: obj.age,
    city: obj.city.trim(),
    avatarUrl: obj.avatarUrl,
    completionPercent: obj.completionPercent,
  });
}

/**
 * Validate a MyProfileSummary.
 * Returns null if valid, otherwise an error message.
 *
 * @deprecated Use createValidatedProfileSummary instead for fail-closed validation.
 */
export function validateProfile(profile: MyProfileSummary): string | null {
  if (!profile.accountId || profile.accountId.trim().length === 0) {
    return 'Invalid account ID';
  }
  if (!profile.displayName || profile.displayName.trim().length === 0) {
    return 'Invalid display name';
  }
  if (!Number.isFinite(profile.age) || profile.age < 0 || !Number.isInteger(profile.age)) {
    return 'Invalid age';
  }
  if (!profile.city || profile.city.trim().length === 0) {
    return 'Invalid city';
  }
  if (
    profile.completionPercent !== undefined &&
    (!Number.isFinite(profile.completionPercent) ||
      profile.completionPercent < 0 ||
      profile.completionPercent > 100 ||
      !Number.isInteger(profile.completionPercent))
  ) {
    return 'Invalid completion percent';
  }
  return null;
}

/**
 * Check if a profile is immutable (frozen object).
 */
export function isImmutableProfile(profile: MyProfileSummary): boolean {
  return Object.isFrozen(profile);
}
